import { apiConfig } from './ApiConfig';
import { InspectionScheduleLineItem } from '@/types/response/InspectionScheduleResponses';
import { AddInspectionLineItemResponse, FileResponse, InspectionResultGroupResponse, InspectionLineItemResponse, ContractDetailsResponse, ReportingInspectionResultGroupResponse } from '@/types/response/InspectionResultResponses';
import { CorrectiveActionGroupResponse, CorrectiveActionLineItemResponse, CorrectiveActionResponseEntry } from '@/types/response/CorrectiveActionResponses';
import { ClassificationsResponse, ExpertiseAreasResponse, FrequenciesResponse, GroupsResponse, InspectionMethodsResponse, InspectorsResponse, SitesResponse, TransactionTypesResponse, UsersResponse, UserTypesResponse } from '@/types/response/lookupResponses';
import { LoginResponse, UserPreferencesResponse } from '@/types/response/userResponses';
import {  FileAttachment } from '@/types/model/inspection-modal';
import { InspectionSaveRequest, ApproveInspectionRequest } from '@/types/request/InspectionRequests';
import { CorrectiveActionSaveRequest } from '@/types/request/CorrectiveActionRequests';
import {  
  GetInspectionPlansResponse,
  GetLineItemsResponse, 
} from '@/types/response/InspectionPlanningResponses';
import { 
  CopyPasteInspectionPlanRequest, 
  CreateGroupRequest, 
  UpdateGroupRequest,
  CreateLineItemRequest, 
  UpdateLineItemRequest,
  UpdateInspectionPlanRequest,
  CheckLineItemExistsRequest
} from '@/types/request/InspectionPlanningRequests';
import {
  SaveUserDetailsRequest,
  SaveUserAssignmentRequest,
  SaveUserPrivilegesRequest,
  ResetUserPasswordRequest,
  SaveUserPreferencesRequest,
} from '@/types/request/userManagementRequests';
import { UserManagementUserResponse } from '@/types/response/userManagementResponses';
import {
  OnlineUserResponse,
  LicenseInfoResponse,
  EnterprisePreferencesResponse,
  LoginImageResponse,
  OfflineAppSettingsResponse,
} from '@/types/response/enterpriseResponses';
import { DashboardToDoResponse } from '@/types/response/TrendAnalysisResponses';
import {
  SendMessageRequest,
  SaveLicenseInfoRequest,
  SavePasswordPolicyRequest,
  SaveEmailPreferencesRequest,
  DeleteLoginImageRequest,
  SaveLoginImageSelectionRequest,
  SaveSignInMessageRequest,
  SaveOfflineAppSettingsRequest,
  AddModuleRequest,
} from '@/types/request/enterpriseRequests';
import {
  ModuleDetailsRequest,
  ReportSettingsRequest,
  TransactionLoggingRequest,
  ScheduleImpactRequest,
  FrequencyUserTypesRequest,
  SaveFrequencyRequest,
  SaveFrequencyProfileRequest,
  DateForwardingRequest,
  SaveAreaRequest,
  SaveSiteRequest,
  ActivateSiteRequest,
  DefaultSiteRequest,
  SaveInProgressRequest,
  ResultsHighlightingRequest,
  SaveClassificationRequest,
  ActivateClassificationRequest,
} from '@/types/request/moduleSettingsRequests';
import _ from 'lodash';


class ApiService {

  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const response = await apiConfig.login(username, password);
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async getSignInMessage(): Promise<string> {
    try {
      return await apiConfig.getSignInMessage();
    } catch (error) {
      console.error('Error fetching sign-in message:', error);
      throw error;
    }
  }

  async forgotPassword(data: { email?: string; username?: string }): Promise<void> {
    try {
      await apiConfig.forgotPassword(data);
    } catch (error) {
      console.error('Error processing forgot password request:', error);
      throw error;
    }
  }

  async getContractInspectionResults(from?: string, to?: string): Promise<InspectionResultGroupResponse[]> {
    try {
      return await apiConfig.getContractInspectionResults(from, to);
    } catch (error) {
      console.error('Error fetching contract inspection results:', error);
      throw error;
    }
  }

  async getReportingInspectionResults(from?: string, to?: string): Promise<ReportingInspectionResultGroupResponse[]> {
    try {
      return await apiConfig.getContractInspectionResults(from, to) as unknown as ReportingInspectionResultGroupResponse[];
    } catch (error) {
      console.error('Error fetching reporting inspection results:', error);
      throw error;
    }
  }

  async getContractInspectionResultsForReport(ids: number[]): Promise<InspectionLineItemResponse[]> {
    try {
      const batchSize = 200;
      const batches = _.chunk(ids, batchSize);
      // const results: InspectionLineItemResponse[] = [];
      // for (const batch of batches) {
      //   const batchResults = await apiConfig.getContractInspectionResultsForReport(batch);
      //   results.push(...batchResults);
      // }
      // return results;
      //call in batches to avoid URL length issues
      const detailedInspections: InspectionLineItemResponse[] = ids.length > 0
        ? (await Promise.all(batches.map(batch => apiConfig.getContractInspectionResultsForReport(batch)))).flat()
        : [];

      return detailedInspections;
    } catch (error) {
      console.error('Error fetching contract inspections:', error);
      throw error;
    }
  }

  async getContractInspection(inspectionResultId: number): Promise<InspectionLineItemResponse> {
    try {
      return await apiConfig.getContractInspection(inspectionResultId);
    } catch (error) {
      console.error('Error fetching inspection:', error);
      throw error;
    }
  }

  async getInspectionResultLineItems(groupId: number): Promise<AddInspectionLineItemResponse[]> {
    try {
      return await apiConfig.getInspectionResultLineItems(groupId);
    } catch (error) {
      console.error('Error fetching inspection result line items:', error);
      throw error;
    }
  }

  async getContracts(): Promise<{ Id: number; Name: string }[]> {
    try {
      return await apiConfig.getContracts();
    } catch (error) {
      console.error('Error fetching contracts:', error);
      throw error;
    }
  }

  async setDefaultContract(contractId: number): Promise<void> {
    try {
      await apiConfig.setDefaultContract(contractId);
    } catch (error) {
      console.error('Error setting default contract:', error);
      throw error;
    }
  }
  
  async getContractFiles(): Promise<FileResponse[]> {
    try {
      return await apiConfig.getContractFiles();
    } catch (error) {
      console.error('Error fetching contract files:', error);
      throw error;
    }
  }

  async getContractUserPreferences(): Promise<UserPreferencesResponse> {
    try {
      return await apiConfig.getContractUserPreferences();
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      throw error;
    }
  }

  async getContractSites(): Promise<SitesResponse[]> {
    try {
      return await apiConfig.getContractSites();
    } catch (error) {
      console.error('Error fetching contract sites:', error);
      throw error;
    }
  }

  async getContractAreasOfExpertise(): Promise<ExpertiseAreasResponse[]> {
    try {
      return await apiConfig.getContractAreasOfExpertise();
    } catch (error) {
      console.error('Error fetching contract areas of expertise:', error);
      throw error;
    }
  }

  async getContractCorrectiveActions(): Promise<CorrectiveActionGroupResponse[]> {
    try {
      return await apiConfig.getContractCorrectiveActions();
    } catch (error) {
      console.error('Error fetching contract corrective actions:', error);
      throw error;
    }
  }

  async getContractCorrectiveAction(correctiveActionId: number): Promise<CorrectiveActionLineItemResponse> {
    try {      
      return await apiConfig.getContractCorrectiveAction(correctiveActionId);
    } catch (error) {
      console.error('Error fetching contract corrective action:', error);
      throw error;
    }
  }


  async getContractInspectors(): Promise<InspectorsResponse[]> {
    try {
      return await apiConfig.getContractInspectors();
    } catch (error) {
      console.error('Error fetching contract inspectors:', error);
      throw error;
    }
  }

  async getContractGroups(): Promise<GroupsResponse[]> {
    try {
      return await apiConfig.getContractGroups(); 
    } catch (error) {
      console.error('Error fetching contract groups:', error);
      throw error;
    } 
  }

  async getContractDashboardToDo(groupIds?: number[]): Promise<DashboardToDoResponse> {
    try {
      return await apiConfig.getContractDashboardToDo(groupIds);
    } catch (error) {
      console.error('Error fetching contract dashboard to-do data:', error);
      throw error;
    }
  }

  async getInspectionScheduleLineItems(from: string, to: string): Promise<InspectionScheduleLineItem[]> {
    try {
      return await apiConfig.getInspectionScheduleLineItems(from, to);
    } catch (error) {
      console.error('Error fetching inspection schedule:', error);
      throw error;
    }
  }

  async getAllInspectionScheduleLineItems(): Promise<InspectionScheduleLineItem[]> {
    try {
      return await apiConfig.getAllInspectionScheduleLineItems();
    } catch (error) {
      console.error('Error fetching all inspection schedule line items:', error);
      throw error;
    }
  }

  async getContractLineItems(): Promise<GetLineItemsResponse> {
    try {
      return await apiConfig.getContractLineItems();
    } catch (error) {
      console.error('Error fetching contract line items:', error);
      throw error;
    }
  }

  async getContractClassifications(): Promise<ClassificationsResponse[]> {
    try {
      return await apiConfig.getContractClassifications();
    } catch (error) {
      console.error('Error fetching contract classifications:', error);
      throw error;
    }
  }
  

  async saveInspection(inspection: InspectionSaveRequest): Promise<void> {
    try {
      await apiConfig.saveInspection(inspection);
    } catch (error) {
      console.error('Error saving inspection:', error);
      throw error;
    }
  }

  async approveInspection(data: ApproveInspectionRequest): Promise<void> {
    try {
      await apiConfig.approveInspection(data);
    } catch (error) {
      console.error('Error approving inspection:', error);
      throw error;
    }
  }

  async deleteInspection(inspectionId: number): Promise<void> {
    try {
      await apiConfig.deleteInspection(inspectionId);
    } catch (error) {
      console.error('Error deleting inspection:', error);
      throw error;
    }
  }

  async uploadFile(attachment: FileAttachment): Promise<{ FileId: number }> {
    try {
      const  file: FormData = new FormData();
      if (attachment.file) {
        file.append('file', attachment.file);
      }
      return await apiConfig.uploadFile(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  async saveCorrectiveAction(data: CorrectiveActionSaveRequest): Promise<number> {
    try {
      return await apiConfig.saveCorrectiveAction(data);
    } catch (error) {
      console.error('Error saving corrective action:', error);
      throw error;
    }
  }

  async deleteCorrectiveAction(correctiveActionId: number): Promise<number> {
    try {
      return await apiConfig.deleteCorrectiveAction({ Id: correctiveActionId });
    } catch (error) {
      console.error('Error deleting corrective action:', error);
      throw error;
    }
  }

  async getCorrectiveActionResponses(correctiveActionId: number): Promise<CorrectiveActionResponseEntry[]> {
    try {
      return await apiConfig.getCorrectiveActionResponses(correctiveActionId);
    } catch (error) {
      console.error('Error fetching corrective action responses:', error);
      throw error;
    }
  }

  async saveCorrectiveActionResponse(data: {
    comments: string;
    statusId: number;
    correctiveActionId: number;
    attachments: { Add: any[]; Edit: any[]; Delete: any[] };
  }): Promise<void> {
    try {
      return await apiConfig.saveCorrectiveActionResponse(data);
    } catch (error) {
      console.error('Error saving corrective action response:', error);
      throw error;
    }
  }

  async getContractFrequencies(): Promise<FrequenciesResponse[]> {
    try {
      return await apiConfig.getContractFrequencies();
    } catch (error) {
      console.error('Error fetching contract frequencies:', error);
      throw error;
    }
  }

  async getUserTypes(): Promise<UserTypesResponse[]> {
    try {
      return await apiConfig.getUserTypes();
    } catch (error) {
      console.error('Error fetching user types:', error);
      throw error;
    }
  }

  async getTransactionTypes(): Promise<TransactionTypesResponse[]> {
    try {
      return await apiConfig.getTransactionTypes();
    } catch (error) {
      console.error('Error fetching transaction types:', error);
      throw error;
    } 
  }

  async getContractUsers(): Promise<UsersResponse[]> {
    try {
      return await apiConfig.getContractUsers();
    } catch (error) {
      console.error('Error fetching contract users:', error);
      throw error;
    } 
  }

  async getInspectionMethods(): Promise<InspectionMethodsResponse[]> {
    try {
      return await apiConfig.getInspectionMethods();  
    } catch (error) {
      console.error('Error fetching inspection methods:', error);
      throw error;
    } 
  }

  // Inspection Planning Methods
  async copyPasteInspectionPlan(data: CopyPasteInspectionPlanRequest): Promise<number> {
    try {
      return await apiConfig.copyPasteInspectionPlan(data);
    } catch (error) {
      console.error('Error copying and pasting inspection plan:', error);
      throw error;
    }
  }

  async createGroup(data: CreateGroupRequest): Promise<number> {
    try {
      return await apiConfig.createGroup(data);
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  }

  async updateGroup(data: UpdateGroupRequest): Promise<number> {
    try {
      return await apiConfig.updateGroup(data);
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  }

  async createLineItem(data: CreateLineItemRequest): Promise<number> {
    try {
      return await apiConfig.createLineItem(data);
    } catch (error) {
      console.error('Error creating line item:', error);
      throw error;
    }
  }

  async updateLineItem(data: UpdateLineItemRequest): Promise<number> {
    try {
      return await apiConfig.updateLineItem(data);
    } catch (error) {
      console.error('Error updating line item:', error);
      throw error;
    }
  }

  async checkLineItemExists(data: CheckLineItemExistsRequest): Promise<boolean> {
    try {
      return await apiConfig.checkLineItemExists(data);
    } catch (error) {
      console.error('Error checking line item identifier uniqueness:', error);
      throw error;
    }
  }

  async activateLineItem(id: number): Promise<number> {
    try {
      return await apiConfig.activateLineItem(id);
    } catch (error) {
      console.error('Error activating line item:', error);
      throw error;
    }
  }

  async inactivateLineItem(id: number, reason: string): Promise<number> {
    try {
      return await apiConfig.inactivateLineItem(id, reason);
    } catch (error) {
      console.error('Error inactivating line item:', error);
      throw error;
    }
  }

  async getInspectionPlans(): Promise<GetInspectionPlansResponse> {
    try {
      return await apiConfig.getInspectionPlans();
    } catch (error) {
      console.error('Error fetching inspection plans:', error);
      throw error;
    }
  }

  async resetInspectionPlan(inspectionPlanIds: number[]): Promise<number> {
    try {
      return await apiConfig.resetInspectionPlan(inspectionPlanIds);
    } catch (error) {
      console.error('Error resetting inspection plan:', error);
      throw error;
    }
  }

  async activateDeactivateInspectionPlan(activeIds: number[], inactiveIds: number[]): Promise<number> {
    try {
      return await apiConfig.activateDeactivateInspectionPlan(activeIds, inactiveIds);
    } catch (error) {
      console.error('Error activating/deactivating inspection plan:', error);
      throw error;
    }
  }

  async lockUnlockInspectionPlan(lockIds: number[], unlockIds: number[], reason: string): Promise<number> {
    try {
      return await apiConfig.lockUnlockInspectionPlan(lockIds, unlockIds, reason);
    } catch (error) {
      console.error('Error locking/unlocking inspection plan:', error);
      throw error;
    }
  }

  async updateInspectionPlan(data: UpdateInspectionPlanRequest): Promise<number> {
    try {
      return await apiConfig.updateInspectionPlan(data);
    } catch (error) {
      console.error('Error updating inspection plan:', error);
      throw error;
    }
  }

  generateAttachmentDownloadUrl(fileId: number): string {
    return apiConfig.generateAttachmentDownloadUrl(fileId);
  }

  generateAttachmentPreviewUrl(attachmentId: number, fileId: number, previewSize: number = 400): string {
    return apiConfig.generateAttachmentPreviewUrl(attachmentId, fileId, previewSize);
  }

  async getContractDetails(): Promise<ContractDetailsResponse> {
    try {
      return await apiConfig.getContractDetails();
    } catch (error) {
      console.error('Error fetching contract details:', error);
      throw error;
    }
  }

  async updateEmailAddress(emailAddress: string): Promise<void> {
    try {
      await apiConfig.updateEmailAddress(emailAddress);
    } catch (error) {
      console.error('Error updating email address:', error);
      throw error;
    }
  }

  async changeAccountPassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await apiConfig.changeAccountPassword(currentPassword, newPassword);
    } catch (error) {
      console.error('Error changing account password:', error);
      throw error;
    }
  }

  async saveUserPreferences(data: object): Promise<number> {
    try {
      return await apiConfig.saveUserPreferences(data);
    } catch (error) {
      console.error('Error saving user preferences:', error);
      throw error;
    }
  }

  async resetUserPreferences(): Promise<void> {
    try {
      await apiConfig.resetUserPreferences();
    } catch (error) {
      console.error('Error resetting user preferences:', error);
      throw error;
    }
  }

  // Module Settings methods
  async getContractImages(): Promise<string[]> {
    try {
      return await apiConfig.getContractImages();
    } catch (error) {
      console.error('Error fetching contract images:', error);
      throw error;
    }
  }

  async saveModuleDetails(data: ModuleDetailsRequest): Promise<void> {
    try {
      await apiConfig.saveModuleDetails(data);
    } catch (error) {
      console.error('Error saving module details:', error);
      throw error;
    }
  }

  async uploadModuleImage(file: FormData): Promise<void> {
    try {
      await apiConfig.uploadModuleImage(file);
    } catch (error) {
      console.error('Error uploading module image:', error);
      throw error;
    }
  }

  async deleteModuleImage(filename: string): Promise<void> {
    try {
      await apiConfig.deleteModuleImage(filename);
    } catch (error) {
      console.error('Error deleting module image:', error);
      throw error;
    }
  }

  async saveModuleLogo(filename: string): Promise<void> {
    try {
      await apiConfig.saveModuleLogo(filename);
    } catch (error) {
      console.error('Error saving module logo:', error);
      throw error;
    }
  }

  async saveReportSettings(data: ReportSettingsRequest): Promise<void> {
    try {
      await apiConfig.saveReportSettings(data);
    } catch (error) {
      console.error('Error saving report settings:', error);
      throw error;
    }
  }

  async saveTransactionLogging(data: TransactionLoggingRequest): Promise<void> {
    try {
      await apiConfig.saveTransactionLogging(data);
    } catch (error) {
      console.error('Error saving transaction logging:', error);
      throw error;
    }
  }

  async saveScheduleImpact(data: ScheduleImpactRequest): Promise<void> {
    try {
      await apiConfig.saveScheduleImpact(data);
    } catch (error) {
      console.error('Error saving schedule impact:', error);
      throw error;
    }
  }

  async saveFrequencyUserTypes(data: FrequencyUserTypesRequest): Promise<void> {
    try {
      await apiConfig.saveFrequencyUserTypes(data);
    } catch (error) {
      console.error('Error saving frequency user types:', error);
      throw error;
    }
  }


  async saveFrequency(data: SaveFrequencyRequest): Promise<void> {
    try {
      await apiConfig.saveFrequency(data);
    } catch (error) {
      console.error('Error saving frequency:', error);
      throw error;
    }
  }

  async saveFrequencyProfile(data: SaveFrequencyProfileRequest): Promise<void> {
    try {
      await apiConfig.saveFrequencyProfile(data);
    } catch (error) {
      console.error('Error saving frequency profile:', error);
      throw error;
    }
  }

  async saveDateForwarding(data: DateForwardingRequest): Promise<void> {
    try {
      await apiConfig.saveDateForwarding(data);
    } catch (error) {
      console.error('Error saving date forwarding:', error);
      throw error;
    }
  }

  async saveExpertiseArea(data: SaveAreaRequest): Promise<void> {
    try {
      await apiConfig.saveExpertiseArea(data);
    } catch (error) {
      console.error('Error saving expertise area:', error);
      throw error;
    }
  }

  async saveSite(data: SaveSiteRequest): Promise<void> {
    try {
      await apiConfig.saveSite(data);
    } catch (error) {
      console.error('Error saving site:', error);
      throw error;
    }
  }

  async activateSite(data: ActivateSiteRequest): Promise<void> {
    try {
      await apiConfig.activateSite(data);
    } catch (error) {
      console.error('Error activating/inactivating site:', error);
      throw error;
    }
  }

  async saveDefaultSite(data: DefaultSiteRequest): Promise<void> {
    try {
      await apiConfig.saveDefaultSite(data);
    } catch (error) {
      console.error('Error saving default site:', error);
      throw error;
    }
  }

  async saveSaveInProgress(data: SaveInProgressRequest): Promise<void> {
    try {
      await apiConfig.saveSaveInProgress(data);
    } catch (error) {
      console.error('Error saving save-in-progress settings:', error);
      throw error;
    }
  }

  async saveResultsHighlighting(data: ResultsHighlightingRequest): Promise<void> {
    try {
      await apiConfig.saveResultsHighlighting(data);
    } catch (error) {
      console.error('Error saving results highlighting:', error);
      throw error;
    }
  }

  async saveClassification(data: SaveClassificationRequest): Promise<void> {
    try {
      await apiConfig.saveClassification(data);
    } catch (error) {
      console.error('Error saving classification:', error);
      throw error;
    }
  }

  async activateClassification(data: ActivateClassificationRequest): Promise<void> {
    try {
      await apiConfig.activateClassification(data);
    } catch (error) {
      console.error('Error activating/inactivating classification:', error);
      throw error;
    }
  }

  async importContractData(file: FormData, importType: 1 | 2): Promise<void> {
    try {
      await apiConfig.importContractData(file, importType);
    } catch (error) {
      console.error('Error importing contract data:', error);
      throw error;
    }
  }

  async getManagementUsers(): Promise<UserManagementUserResponse[]> {
    try {
      return await apiConfig.getManagementUsers();
    } catch (error) {
      console.error('Error fetching management users:', error);
      throw error;
    }
  }

  async saveUserDetails(data: SaveUserDetailsRequest): Promise<void> {
    try {
      await apiConfig.saveUserDetails(data);
    } catch (error) {
      console.error('Error saving user details:', error);
      throw error;
    }
  }

  async saveUserAssignment(data: SaveUserAssignmentRequest): Promise<void> {
    try {
      await apiConfig.saveUserAssignment(data);
    } catch (error) {
      console.error('Error saving user assignment:', error);
      throw error;
    }
  }

  async saveUserPrivileges(data: SaveUserPrivilegesRequest): Promise<void> {
    try {
      await apiConfig.saveUserPrivileges(data);
    } catch (error) {
      console.error('Error saving user privileges:', error);
      throw error;
    }
  }

  async resetUserPassword(data: ResetUserPasswordRequest): Promise<void> {
    try {
      await apiConfig.resetUserPassword(data);
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw error;
    }
  }

  async saveUserManagementPreferences(data: SaveUserPreferencesRequest): Promise<void> {
    try {
      await apiConfig.saveUserManagementPreferences(data);
    } catch (error) {
      console.error('Error saving user management preferences:', error);
      throw error;
    }
  }

  async getOnlineUsers(): Promise<OnlineUserResponse[]> {
    try {
      return await apiConfig.getOnlineUsers();
    } catch (error) {
      console.error('Error fetching online users:', error);
      throw error;
    }
  }

  async sendMessage(data: SendMessageRequest): Promise<void> {
    try {
      await apiConfig.sendMessage(data);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async getLicenseInfo(): Promise<LicenseInfoResponse> {
    try {
      return await apiConfig.getLicenseInfo();
    } catch (error) {
      console.error('Error fetching license info:', error);
      throw error;
    }
  }

  async saveLicenseInfo(data: SaveLicenseInfoRequest): Promise<void> {
    try {
      await apiConfig.saveLicenseInfo(data);
    } catch (error) {
      console.error('Error saving license info:', error);
      throw error;
    }
  }

  async getEnterprisePreferences(): Promise<EnterprisePreferencesResponse> {
    try {
      return await apiConfig.getEnterprisePreferences();
    } catch (error) {
      console.error('Error fetching enterprise preferences:', error);
      throw error;
    }
  }

  async savePasswordPolicy(data: SavePasswordPolicyRequest): Promise<void> {
    try {
      await apiConfig.savePasswordPolicy(data);
    } catch (error) {
      console.error('Error saving password policy:', error);
      throw error;
    }
  }

  async saveEmailPreferences(data: SaveEmailPreferencesRequest): Promise<void> {
    try {
      await apiConfig.saveEmailPreferences(data);
    } catch (error) {
      console.error('Error saving email preferences:', error);
      throw error;
    }
  }

  async getLoginImages(): Promise<LoginImageResponse[]> {
    try {
      return await apiConfig.getLoginImages();
    } catch (error) {
      console.error('Error fetching login images:', error);
      throw error;
    }
  }

  async uploadLoginImage(file: FormData): Promise<void> {
    try {
      await apiConfig.uploadLoginImage(file);
    } catch (error) {
      console.error('Error uploading login image:', error);
      throw error;
    }
  }

  async deleteLoginImage(data: DeleteLoginImageRequest): Promise<void> {
    try {
      await apiConfig.deleteLoginImage(data);
    } catch (error) {
      console.error('Error deleting login image:', error);
      throw error;
    }
  }

  async saveLoginImageSelection(data: SaveLoginImageSelectionRequest): Promise<void> {
    try {
      await apiConfig.saveLoginImageSelection(data);
    } catch (error) {
      console.error('Error saving login image selection:', error);
      throw error;
    }
  }

  async saveSignInMessage(data: SaveSignInMessageRequest): Promise<void> {
    try {
      await apiConfig.saveSignInMessage(data);
    } catch (error) {
      console.error('Error saving sign-in message:', error);
      throw error;
    }
  }

  async getOfflineAppSettings(): Promise<OfflineAppSettingsResponse> {
    try {
      return await apiConfig.getOfflineAppSettings();
    } catch (error) {
      console.error('Error fetching offline app settings:', error);
      throw error;
    }
  }

  async saveOfflineAppSettings(data: SaveOfflineAppSettingsRequest): Promise<void> {
    try {
      await apiConfig.saveOfflineAppSettings(data);
    } catch (error) {
      console.error('Error saving offline app settings:', error);
      throw error;
    }
  }

  async addModule(data: AddModuleRequest): Promise<void> {
    try {
      await apiConfig.addModule(data);
    } catch (error) {
      console.error('Error adding module:', error);
      throw error;
    }
  }

  getBaseUrl(): string {
    return apiConfig.getBaseUrl();
  }
}

export const apiService = new ApiService();